import { AnimatePresence, motion } from "framer-motion";
import { Edit2, Plus, Trash2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { createBed, deleteBed, getBeds, getSectors, updateBed, getStatuses } from "../../services/api";

export function BedManagementSettings() {
    const [beds, setBeds] = useState([]);
    const [sectors, setSectors] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingBed, setEditingBed] = useState(null);
    const [formData, setFormData] = useState({ number: "", sectorId: "" });
    const [filterSector, setFilterSector] = useState("all");

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [bedsRes, sectorsRes, statusesRes] = await Promise.all([
                getBeds(),
                getSectors(),
                getStatuses()
            ]);
            setBeds(bedsRes.data);
            setSectors(sectorsRes.data);
            setStatuses(statusesRes.data);
        } catch (error) {
            console.error("Erro ao buscar dados:", error);
            alert("Erro ao buscar dados");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleOpenModal = (bed = null) => {
        if (bed) {
            setEditingBed(bed);
            setFormData({ number: bed.number, sectorId: bed.sectorId });
        } else {
            setEditingBed(null);
            // Default to first sector if available
            setFormData({ number: "", sectorId: sectors.length > 0 ? sectors[0].id : "" });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingBed(null);
        setFormData({ number: "", sectorId: "" });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                number: formData.number,
                sectorId: parseInt(formData.sectorId),
            };

            if (editingBed) {
                await updateBed(editingBed.id, payload);
                alert("Leito atualizado com sucesso!");
            } else {
                await createBed(payload);
                alert("Leito criado com sucesso!");
            }
            handleCloseModal();
            fetchData();
        } catch (error) {
            console.error("Erro ao salvar leito:", error);
            alert("Erro ao salvar leito. Tente novamente.");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Tem certeza que deseja excluir este leito?")) {
            try {
                await deleteBed(id);
                alert("Leito excluído com sucesso!");
                fetchData();
            } catch (error) {
                // Se der 404, significa que já foi excluído (comportamento desejado para 'limpar' fantasmas)
                if (error.response && error.response.status === 404) {
                    alert("Leito não encontrado no servidor, mas foi removido da lista.");
                    fetchData(); // Recarrega para limpar
                } else {
                    console.error("Erro ao excluir leito:", error);
                    alert(error.response?.data?.error || "Erro ao excluir leito.");
                }
            }
        }
    };

    const filteredBeds = filterSector === "all"
        ? beds
        : beds.filter(b => b.sectorId === parseInt(filterSector));

    const sortedBeds = [...filteredBeds].sort((a, b) => a.id - b.id);

    const getStatusColor = (statusId) => {
        const status = statuses.find(s => s.id === statusId);
        if (!status) return 'bg-gray-100 text-gray-800'; // Default

        // Mapear cores baseadas no nome ou ID
        switch (status.id) {
            case 1: return 'bg-green-100 text-green-800'; // Disponível
            case 2: return 'bg-yellow-100 text-yellow-800'; // Ocupado
            case 3: return 'bg-orange-100 text-orange-800'; // Manutenção
            case 4: return 'bg-blue-100 text-blue-800'; // Higienização
            case 8: return 'bg-purple-100 text-purple-800'; // Aguardando Limpeza
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusName = (statusId) => {
        const status = statuses.find(s => s.id === statusId);
        return status ? status.name : `ID: ${statusId}`;
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Gerenciar Leitos</h2>

                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                    <select
                        value={filterSector}
                        onChange={(e) => setFilterSector(e.target.value)}
                        className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-black/20 focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white"
                    >
                        <option value="all">Todos os Setores</option>
                        {sectors.map(sector => (
                            <option key={sector.id} value={sector.id}>{sector.name}</option>
                        ))}
                    </select>

                    <button
                        onClick={() => handleOpenModal()}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors justify-center"
                    >
                        <Plus size={20} />
                        Adicionar Leito
                    </button>
                </div>
            </div>

            <div className="bg-white/50 dark:bg-black/20 backdrop-blur-sm rounded-xl border border-gray-200/50 dark:border-gray-800 p-1">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-800">
                            <th className="p-4 font-medium text-gray-500 dark:text-gray-400">ID</th>
                            <th className="p-4 font-medium text-gray-500 dark:text-gray-400">Número/Nome</th>
                            <th className="p-4 font-medium text-gray-500 dark:text-gray-400">Setor</th>
                            <th className="p-4 font-medium text-gray-500 dark:text-gray-400">Status</th>
                            <th className="p-4 font-medium text-gray-500 dark:text-gray-400 text-right">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        <AnimatePresence>
                            {sortedBeds.map((bed) => {
                                const bedSector = sectors.find(s => s.id === bed.sectorId);
                                return (
                                    <motion.tr
                                        key={bed.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0 }}
                                        className="border-b border-gray-100 dark:border-gray-800/50 last:border-0 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                                    >
                                        <td className="p-4 text-gray-600 dark:text-gray-300">#{bed.id}</td>
                                        <td className="p-4 font-medium text-gray-800 dark:text-white">{bed.number}</td>
                                        <td className="p-4 text-gray-600 dark:text-gray-300">{bedSector?.name || "Setor desconhecido"}</td>
                                        <td className="p-4">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(bed.statusId)}`}>
                                                {getStatusName(bed.statusId)}
                                            </span>
                                        </td>
                                        <td className="p-4 text-right flex justify-end gap-2">
                                            <button
                                                onClick={() => handleOpenModal(bed)}
                                                className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                                title="Editar"
                                            >
                                                <Edit2 size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(bed.id)}
                                                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                                title="Excluir"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </td>
                                    </motion.tr>
                                );
                            })}
                        </AnimatePresence>
                        {sortedBeds.length === 0 && !isLoading && (
                            <tr>
                                <td colSpan="5" className="p-8 text-center text-gray-500">
                                    Nenhum leito encontrado
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-800"
                    >
                        <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
                            {editingBed ? "Editar Leito" : "Novo Leito"}
                        </h3>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Número/Nome do Leito
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.number}
                                    onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white"
                                    placeholder="Ex: 201-A"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Setor
                                </label>
                                <select
                                    required
                                    value={formData.sectorId}
                                    onChange={(e) => setFormData({ ...formData, sectorId: e.target.value })}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white appearance-none"
                                >
                                    <option value="" disabled>Selecione um setor</option>
                                    {sectors.map(sector => (
                                        <option key={sector.id} value={sector.id}>{sector.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 rounded-lg transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                                >
                                    Salvar
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}
        </div>
    );
}
